import Config from '../../Config';

/**
 * Waits until the reCAPTCHA script is fully loaded and grecaptcha is available.
 * @returns {Promise<typeof grecaptcha>}
 */
const waitForGrecaptcha = () => {
  return new Promise((resolve) => {
    const check = () => {
      if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
        resolve(window.grecaptcha);
      } else {
        setTimeout(check, 100);
      }
    };
    check();
  });
};

/**
 * Resolves a reCAPTCHA token for a given action type.
 * @param {string} actionType - e.g., 'login', 'submit', 'social', etc.
 * @returns {Promise<string>} - The reCAPTCHA token
 */
const resolveRecaptchaPromise = (actionType) => {
  return waitForGrecaptcha().then((grecaptcha) => {
    return new Promise((resolve, reject) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(Config.captchaSiteKey, {action: actionType}).then(resolve).catch(reject);
      });
    });
  });
};

/**
 * Gets a reCAPTCHA token for the default action 'submit'.
 * @returns {Promise<string>}
 */
export const recaptcha = async () => {
  return getRecaptchaToken('submit');
};

/**
 * Gets a reCAPTCHA token for a specific action type.
 * @param {string} actionType
 * @returns {Promise<string>}
 */
export const getRecaptcha = async (actionType) => {
  return getRecaptchaToken(actionType);
};

/**
 * Main exported function to get a reCAPTCHA token safely.
 * @param {string} actionType
 * @returns {Promise<string>}
 */
export const getRecaptchaToken = async (actionType = 'submit') => {
  try {
    const token = await resolveRecaptchaPromise(actionType);
    return token;
  } catch (error) {
    console.error('[zohar] Failed to get reCAPTCHA token:', error);
    throw error;
  }
};
